import React, { useEffect } from 'react';
import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import PropTypes from 'prop-types';
import {
  Block,
  Select,
  FormGroup,
  useForm,
  BoolSelect,
  Yup
} from '../components';
import { useTranslation } from 'react-i18next';
import { Form, Col, Row } from 'react-bootstrap';
import GeojsonFilterForm from "./GeojsonFilterForm";
import CoordinatesFilterForm from "./CoordinatesFilterForm";
import AdminBoundariesFilterForm from "./AdminBoundariesFilterForm";
import {AdminBoundariesToArray} from "../../services/zones";

function customMerge(objValue, srcValue) {
  if (isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}

function ZoneForm(props) {
	const { t } = useTranslation();

	const {formData, setFormData, getInputProps, formProps, submitButtonProps} = useForm({
		defaultObject: props.zone,
    initialValues: {
			name: '',
      save_standalone: props?.fromStream ? ((obj) => obj && Object.keys(obj).length > 0 ? false : '') : true,
    	filter_type: (obj) => obj?.type ? obj.type : '',
    	coordinates: (obj) => obj?.type === 'coordinates' ? obj?.content : {},
      geojson: (obj) => obj?.type === 'geojson' ? obj.content : '',
      admin_boundaries: (obj) => obj?.type === 'admin_boundaries' ? AdminBoundariesToArray(obj.content) : []
		},
    validationSchema: Yup.object().shape({
      name: Yup.string().when('save_standalone', {
        is: true,
        then: Yup.string().required()
      }),
      filter_type: Yup.string().required(),
      admin_boundaries: Yup.array(Yup.object()).when('filter_type', {
        is: 'admin_boundaries',
        then: Yup.array().min(1)
      }),
      geojson: Yup.mixed().when('filter_type', {
        is: 'geojson',
        then: Yup.object().geojson().required()
      }),
      save_standalone: Yup.bool()
    }),
    disabled: props.disabled,
    parentFormRef: props.parentFormRef,
    preProcess: (data) => {
		  var content;
		  if (data.filter_type === 'admin_boundaries') {
		    content = data.admin_boundaries.reduce((acc, value) => {
          const processedValue = {[value.level]: value.level == 0 ? [value.value] : {[value.country]: [value.value]}}
		      return mergeWith(acc, processedValue, customMerge)
        }, {})
      } else {
        content = data[data.filter_type]
      }
      return {
        name: data.name,
        type: data.filter_type,
        content: content,
        save_standalone: data.save_standalone
      }
    },
    setSubmitButtonProps: props.setSubmitButtonProps,
    onSubmit: props.handleSubmit
	})

  useEffect(() => {
    setFormData(props.zone)
  }, [props.zone])

  return (
    <Form {...formProps} {...props.formProps}>
      <Block>
        <Form.Row>
          {props.fromStream && <FormGroup
            as={Col}
            md={6}
            label={t("zone.save")}
            input={BoolSelect}
            {...getInputProps("save_standalone")}/>}
          {(formData.save_standalone || !props.fromStream) && <FormGroup
            as={Col}
            md={6}
            label={t("zone.name")}
            input="text"
            {...getInputProps("name")}/>}
        </Form.Row>
        <Form.Row>
          <FormGroup as={Col} md={6} label={t("zone.filterType")} {...getInputProps("filter_type")}>
            <Select>
              <Option value="admin_boundaries">{t("zone.adminBoundaries")}</Option>
              <Option value="geojson">{t("zone.geojson")}</Option>
              <Option value="coordinates">{t("zone.coordinates.title")}</Option>
            </Select>
          </FormGroup>
        </Form.Row>
      </Block>
      {formData.filter_type === 'admin_boundaries' && <AdminBoundariesFilterForm {...getInputProps('admin_boundaries')} />}
      {formData.filter_type === 'geojson' && <GeojsonFilterForm {...getInputProps('geojson')}  />}
      {formData.filter_type === 'coordinates' && <CoordinatesFilterForm {...getInputProps('coordinates')}/>}
    </Form>
	)
}

export default ZoneForm

ZoneForm.propTypes = {
  onChange: PropTypes.func,
  zone: PropTypes.shape({
    name: PropTypes.string,
    save_standalone: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([''])]),
    filter_type: PropTypes.string,
    country: PropTypes.string,
    countries: PropTypes.arrayOf(PropTypes.string),
    level_1: PropTypes.arrayOf(PropTypes.string),
    coordinates: PropTypes.shape({
      radius: PropTypes.number,
      coordinates: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          key: PropTypes.string,
          latitude: PropTypes.number,
          longitude: PropTypes.number,
          radius: PropTypes.number
        })
      )
    })
  }),
  parentFormRef: PropTypes.object,
  fromStream: PropTypes.bool,
  disabled: PropTypes.bool
}

ZoneForm.defaultProps = {
  fromStream: false,
  disabled: false
};
